import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setBearer, clearBearer } from "@/lib/api";

const AuthCtx = createContext(null);
const SS_KEY = "nvc_session_bearer";

async function sendTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    await api.put("/user/timezone", { timezone: tz });
  } catch (e) {
    console.warn("Failed to send timezone", e?.message || e);
  }
}

function readSession() {
  try {
    return window.sessionStorage.getItem(SS_KEY);
  } catch (e) {
    return null;
  }
}

function writeSession(token) {
  try {
    if (token) window.sessionStorage.setItem(SS_KEY, token);
    else window.sessionStorage.removeItem(SS_KEY);
  } catch (e) { /* private mode may block sessionStorage — ignore */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);

  useEffect(() => {
    // Migration: remove any legacy localStorage token from before the httpOnly cookie switch.
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.removeItem("nvc_token");
      }
    } catch (e) { /* ignore */ }

    // Restore Bearer fallback from sessionStorage (survives reload, cleared on tab close).
    // This is only used when the httpOnly cookie can't be delivered (e.g. cross-site iframe).
    const restored = readSession();
    if (restored) setBearer(restored);

    fetchMe()
      .then(() => sendTimezone())
      .catch((e) => {
        if (e?.response?.status && e.response.status !== 401) {
          console.error("auth bootstrap error", e);
        }
        // Failed restore — clear any stale bearer
        clearBearer();
        writeSession(null);
      })
      .finally(() => setLoading(false));
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Cookie is set httpOnly server-side; also store bearer as fallback for iframe contexts.
    setBearer(data.token);
    writeSession(data.token);
    setUser(data.user);
    sendTimezone();
    return data.user;
  };

  const register = async (name, email, password, language, referral_code) => {
    const payload = { name, email, password, language };
    if (referral_code) payload.referral_code = referral_code;
    const { data } = await api.post("/auth/register", payload);
    setBearer(data.token);
    writeSession(data.token);
    setUser(data.user);
    sendTimezone();
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("logout request failed", e?.message || e);
    }
    clearBearer();
    writeSession(null);
    setUser(null);
  };

  const setLanguageOnServer = async (lang) => {
    if (!user) return;
    try {
      const { data } = await api.put("/auth/language", { language: lang });
      setUser(data);
    } catch (e) {
      console.warn("Failed to update language", e?.message || e);
    }
  };

  const refreshUser = () => fetchMe().catch(() => null);

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, setLanguageOnServer, refreshUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
